"use client";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  ChevronDownIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { RotateCcw, Zap } from "lucide-react";
import { useState, useRef, useEffect, useMemo, memo } from "react";
import { FilterChip } from "./filter-chip";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Logo } from "@/components/images/logo";
import type { BlockConfig } from "@/lib/registry/types";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandDialog,
} from "@/components/ui/command";
import Link from "next/link";
import { getLibraryColorValue } from "@/lib/colors/library-colors";
import { DialogTitle } from "@/components/ui/dialog";
import { routes } from "@/config/routes";
import { MagicCheckbox } from "@/components/ui/magic-checkbox";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

export type ExclusiveMode = "none" | "library" | "category";

interface FilterHeaderProps {
  isScrolled: boolean;
  libraries: string[];
  categories: string[];
  activeLibraries: string[];
  activeCategories: string[];
  availableLibraries: string[];
  availableCategories: string[];
  exclusiveMode: ExclusiveMode;
  onLibraryToggle: (library: string) => void;
  onCategoryToggle: (category: string) => void;
  onShowOnlyLibrary: (library: string) => void;
  onShowOnlyCategory: (category: string) => void;
  onResetLibraries: () => void;
  onResetCategories: () => void;
  onResetAllFilters: () => void;
  components?: BlockConfig[]; // Add components for search
  showDemos: boolean;
  onToggleDemos: (checked: boolean) => void;
}

// Memoized FilterChip wrapper to prevent re-renders
const MemoizedFilterChip = memo(FilterChip);

export function FilterHeader({
  isScrolled,
  libraries,
  categories,
  activeLibraries,
  activeCategories,
  availableLibraries,
  availableCategories,
  exclusiveMode,
  onLibraryToggle,
  onCategoryToggle,
  onShowOnlyLibrary,
  onShowOnlyCategory,
  onResetLibraries,
  onResetCategories,
  onResetAllFilters,
  children,
  components = [], // Default to empty array
  showDemos,
  onToggleDemos,
}: FilterHeaderProps & { children?: React.ReactNode }) {
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  // Add state to track the expanded state of the libraries dropdown
  const [isLibrariesExpanded, setIsLibrariesExpanded] = useState(false);

  // Add keyboard shortcut for opening command menu
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen((open) => !open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Filter components based on search query
  const filteredComponents = searchQuery
    ? components
        .filter(
          (component) =>
            component.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            component.description
              ?.toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            (component.categories || []).some((cat) =>
              cat.toLowerCase().includes(searchQuery.toLowerCase())
            ) ||
            (component.tags || []).some((tag) =>
              tag.toLowerCase().includes(searchQuery.toLowerCase())
            )
        )
        .slice(0, 10)
    : [];

  // Get popular/featured components to show when no search query
  const featuredComponents = !searchQuery
    ? components
        // Filter for components that might have featured/popular properties or fallback to random selection
        .filter(
          (component) =>
            ("featured" in component && component.featured) ||
            ("popular" in component && component.popular)
        )
        .slice(0, 5) ||
      // Fallback to random components if no featured/popular ones exist
      components.sort(() => 0.5 - Math.random()).slice(0, 5)
    : [];

  // Handle toggle of the libraries dropdown
  const handleLibrariesToggle = () => {
    if (isScrolled) {
      setIsLibrariesExpanded(!isLibrariesExpanded);

      // Update DOM attributes for CSS classes that rely on them
      const toggleButton = document.querySelector(
        "[aria-controls='libraries-dropdown']"
      );
      if (toggleButton) {
        toggleButton.setAttribute(
          "data-expanded",
          String(!isLibrariesExpanded)
        );
      }

      const librariesContainer = document.querySelector(
        ".libraries-filter-container"
      );
      if (librariesContainer) {
        librariesContainer.setAttribute(
          "data-expanded",
          String(!isLibrariesExpanded)
        );
      }
    }
  };

  // Memoize the libraries section to prevent re-renders
  const librariesSection = useMemo(() => {
    return [...libraries]
      .sort((a, b) =>
        a.localeCompare(b, undefined, {
          sensitivity: "base",
        })
      )
      .map((library) => (
        <div key={library} className="transition-all duration-300 ease-in-out">
          <MemoizedFilterChip
            label={library}
            isActive={activeLibraries.includes(library)}
            isAvailable={availableLibraries.includes(library)}
            isExclusive={
              exclusiveMode === "library" && activeLibraries.includes(library)
            }
            onToggle={() => onLibraryToggle(library)}
            onExclusive={() => onShowOnlyLibrary(library)}
            compact={isScrolled}
            isLibrary={true}
          />
        </div>
      ));
  }, [
    libraries,
    activeLibraries,
    availableLibraries,
    exclusiveMode,
    onLibraryToggle,
    onShowOnlyLibrary,
    isScrolled,
  ]);

  // Memoize the categories section to prevent re-renders
  const categoriesSection = useMemo(() => {
    return [...categories]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((category) => (
        <MemoizedFilterChip
          key={category}
          label={category}
          isActive={activeCategories.includes(category)}
          isAvailable={availableCategories.includes(category)}
          isExclusive={
            exclusiveMode === "category" && activeCategories.includes(category)
          }
          onToggle={() => onCategoryToggle(category)}
          onExclusive={() => onShowOnlyCategory(category)}
          compact={isScrolled}
        />
      ));
  }, [
    categories,
    activeCategories,
    availableCategories,
    exclusiveMode,
    onCategoryToggle,
    onShowOnlyCategory,
    isScrolled,
  ]);

  // Memoize the active filters section to prevent re-renders
  const activeFiltersSection = useMemo(() => {
    if (activeLibraries.length === 0 && activeCategories.length === 0) {
      return null;
    }

    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-2 transition-all duration-300 ease-in-out",
          "opacity-100 max-h-24"
        )}
      >
        <span className="text-xs text-muted-foreground">Active filters:</span>

        {activeLibraries.map((lib) => (
          <span
            key={`lib-${lib}`}
            className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full transition-all duration-300"
          >
            {lib}
            <button
              type="button"
              onClick={() => onLibraryToggle(lib)}
              className="ml-1 text-primary/70 hover:text-primary transition-colors duration-300"
            >
              <Cross2Icon className="h-3 w-3" />
            </button>
          </span>
        ))}

        {activeCategories.map((cat) => (
          <span
            key={`cat-${cat}`}
            className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full transition-all duration-300"
          >
            {cat}
            <button
              type="button"
              onClick={() => onCategoryToggle(cat)}
              className="ml-1 text-primary/70 hover:text-primary transition-colors duration-300"
            >
              <Cross2Icon className="h-3 w-3" />
            </button>
          </span>
        ))}

        <button
          type="button"
          onClick={onResetAllFilters}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors duration-300"
        >
          Clear all
        </button>
      </div>
    );
  }, [
    activeLibraries,
    activeCategories,
    onLibraryToggle,
    onCategoryToggle,
    onResetAllFilters,
  ]);

  return (
    <>
      {/* Main container with stable min-height to prevent jumps */}
      <div
        className={cn(
          "flex flex-col transition-all duration-300 ease-in-out",
          // Add padding based on scroll state
          isScrolled ? "px-0" : "px-6"
        )}
        style={{
          transitionProperty: "all",
          transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          transitionDuration: "400ms",
        }}
      >
        {/* Main filter container - turns into a single row when scrolled */}
        <div
          className={cn(
            "flex transition-all duration-300 ease-in-out",
            isScrolled
              ? "flex-row items-center justify-between gap-4" // Keep original scrolled layout
              : "flex-col items-start gap-6" // Keep original non-scrolled layout
          )}
        >
          {/* Search bar - only shown when scrolled, styled as in filter-header2 */}
          {isScrolled && (
            <div className="relative w-86 ml-6 mr-2 z-50 flex-shrink-0">
              {/* Use button style from filter-header2 */}
              <button
                type="button"
                onClick={() => setIsSearchOpen(true)}
                className="flex items-center border rounded-md border-input px-1 bg-background h-7 w-full text-xs text-muted-foreground"
              >
                <MagnifyingGlassIcon className="h-4 w-4 shrink-0 mr-2" />
                <span>Search&hellip;</span>
                <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center justify-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </button>
            </div>
          )}

          {/* Command Menu - remains the same */}
          <CommandDialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
            <DialogTitle className="sr-only">Search components</DialogTitle>
            <CommandInput
              placeholder="Search components..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {searchQuery && filteredComponents.length === 0 ? (
                <CommandEmpty>No components found.</CommandEmpty>
              ) : null}

              {!searchQuery && featuredComponents.length > 0 && (
                <CommandGroup heading="Popular Components">
                  {featuredComponents.map((component) => {
                    const href = `/components/${component.library}/${component.name}`;
                    return (
                      <Link
                        key={`${component.library}/${component.name}`}
                        href={href}
                        onClick={() => setIsSearchOpen(false)}
                        className="block"
                      >
                        <CommandItem
                          value={`${component.library}/${component.name}`}
                          onSelect={() => setIsSearchOpen(false)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center">
                            <span
                              className="mr-2 h-3 w-3 rounded-full"
                              style={{
                                backgroundColor: getLibraryColorValue(
                                  component.library as string,
                                  { opacity: 50 }
                                ),
                              }}
                            />
                            <span>{component.name}</span>
                            <span className="ml-2 text-muted-foreground text-xs">
                              {component.library}
                            </span>
                          </div>
                        </CommandItem>
                      </Link>
                    );
                  })}
                </CommandGroup>
              )}

              {filteredComponents.length > 0 && (
                <CommandGroup
                  heading={searchQuery ? "Components" : "All Components"}
                >
                  {filteredComponents.map((component) => {
                    const href = `/components/${component.library}/${component.name}`;
                    return (
                      <Link
                        key={`${component.library}/${component.name}`}
                        href={href}
                        onClick={() => setIsSearchOpen(false)}
                        className="block"
                      >
                        <CommandItem
                          value={`${component.library}/${component.name}`}
                          onSelect={() => setIsSearchOpen(false)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center">
                            <span
                              className="mr-2 h-3 w-3 rounded-full"
                              style={{
                                backgroundColor: getLibraryColorValue(
                                  component.library as string,
                                  { opacity: 50 }
                                ),
                              }}
                            />
                            <span>{component.name}</span>
                            <span className="ml-2 text-muted-foreground text-xs">
                              {component.library}
                            </span>
                          </div>
                        </CommandItem>
                      </Link>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </CommandDialog>

          {/* Main content wrapper - adjusted to match filter-header2 */}
          <div
            className={cn(
              "flex-grow relative min-w-0 w-full",
              isScrolled && "flex items-center" // Keep flex items-center for scrolled state
            )}
          >
            {/* UI Libraries toggle button - kept separate, styled as in filter-header2 */}
            {isScrolled && (
              <div className="flex-shrink-0">
                {" "}
                {/* Add flex-shrink-0 */}
                <div
                  className={cn(
                    "flex items-center gap-1.5 cursor-pointer px-3 rounded-full border transition-all duration-300",
                    "h-7 border-input hover:bg-accent hover:text-accent-foreground group" // Style from filter-header2
                  )}
                  data-expanded={isLibrariesExpanded ? "true" : "false"}
                  onClick={handleLibrariesToggle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleLibrariesToggle();
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-expanded={isLibrariesExpanded ? "true" : "false"}
                  aria-controls="libraries-dropdown"
                >
                  <div className="flex items-center gap-1.5 transition-opacity duration-300">
                    <span className="text-xs text-nowrap">UI Libraries</span>
                    <ChevronDownIcon
                      className={cn(
                        "h-4 w-4 transition-transform duration-300",
                        "group-data-[expanded=true]:rotate-180"
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Filters container (Libraries + Categories) */}
            <div
              className={cn(
                "overflow-hidden flex items-center min-w-0",
                // Apply mask from filter-header2
                isScrolled
                  ? "flex-grow px-4 [mask-image:linear-gradient(to_right,transparent,white_2%,white_95%,transparent)]"
                  : "w-full flex-col px-0" // Adjust padding for non-scrolled
              )}
            >
              {/* Libraries filter and controls row */}
              <div
                className={cn(
                  "relative w-full transition-all duration-300 ease-in-out libraries-filter-container",
                  isScrolled && "absolute z-10", // Positioning from filter-header2
                  isScrolled &&
                    "[&:not([data-expanded=true])]:opacity-0 [&:not([data-expanded=true])]:translate-y-[-100%]",
                  "data-[expanded=true]:opacity-100 data-[expanded=true]:translate-y-0",
                  !isScrolled && "opacity-100 translate-y-0"
                )}
                data-expanded={isLibrariesExpanded ? "true" : "false"}
                style={{
                  transitionProperty: "all",
                  transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                  transitionDuration: "400ms",
                }}
              >
                {/* Inner wrapper for background/padding when scrolled */}
                <div
                  className={cn(
                    "mt-1 transition-all duration-300 ease-in-out",
                    isScrolled ? "bg-accent/10 rounded-lg p-2" : "w-full" // Adjusted padding
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center gap-2 transition-all duration-300",
                      isScrolled ? "flex-wrap" : "justify-between"
                    )}
                  >
                    {/* Libraries section - left side when not scrolled */}
                    <div
                      className={cn(
                        "transition-all duration-300",
                        isScrolled ? "w-full" : "flex-grow"
                      )}
                    >
                      {/* Libraries filter row */}
                      <div className="flex items-center gap-2 transition-all duration-300 ease-in-out">
                        {/* Libraries header - styled as in filter-header2 */}
                        {!isScrolled && (
                          <div className="flex items-center gap-1.5 cursor-pointer h-8 px-3 rounded-full border border-transparent transition-all duration-300">
                            <span className="text-sm font-medium text-foreground/80">
                              Libraries
                            </span>
                          </div>
                        )}

                        {/* Libraries filter chips */}
                        <ScrollArea className="w-full transition-all duration-300">
                          <div className="flex gap-2 pb-1">
                            {librariesSection}
                          </div>
                          <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                      </div>
                    </div>

                    {/* Filter Controls container for non-scrolled state */}
                    {!isScrolled && (
                      // Position controls on the right, ordered first in DOM for flex layout
                      <div className="order-first flex-shrink-0 flex items-center">
                        {/* Use children directly */}
                        {children}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Categories filter row - Adjusted structure */}
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300 min-w-0",
                  isScrolled ? "flex-grow" : "w-full" // Takes full width when not scrolled
                )}
                style={{
                  // Apply transform from filter-header2
                  transform:
                    isScrolled && isLibrariesExpanded
                      ? "translateY(50px)" // Adjusted translateY for scrolled+expanded
                      : "translateY(0)",
                  transitionProperty: "all",
                  transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
                  transitionDuration: "400ms",
                }}
              >
                <div className="flex items-center gap-2 transition-all duration-300 ease-in-out">
                  {/* Categories header - styled as in filter-header2 */}
                  {!isScrolled && categories.length > 0 && (
                    <div className="flex items-center gap-1.5 cursor-pointer h-8 px-3 rounded-full border border-transparent transition-all duration-300">
                      <div className="transition-opacity duration-300">
                        <span className="text-sm font-medium text-foreground/80">
                          Categories
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Categories filter chips */}
                  <ScrollArea className="pl-1 w-full transition-all duration-300">
                    <div id="categories-dropdown" className="flex gap-2">
                      {" "}
                      {/* Added pb-1 */}
                      {categoriesSection}
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>

                  {/* Selected categories indicators for scrolled state */}
                  {isScrolled && activeCategories.length > 0 && (
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      {" "}
                      {/* Added flex-shrink-0 */}
                      <Badge
                        variant="outline"
                        className="text-xs py-0 whitespace-nowrap min-w-fit"
                      >
                        {activeCategories.length} selected
                      </Badge>
                    </div>
                  )}

                  {/* Selected libraries indicators for scrolled state (hidden when expanded) */}
                  {isScrolled && activeLibraries.length > 0 && (
                    // Class from filter-header2 to hide when expanded
                    <div className="flex items-center gap-1 ml-2 group-data-[expanded=false]:block group-data-[expanded=true]:hidden flex-shrink-0">
                      {" "}
                      {/* Added flex-shrink-0 */}
                      <Badge
                        variant="outline"
                        className="text-xs py-0 whitespace-nowrap min-w-fit"
                      >
                        {activeLibraries.length} selected
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Filter Controls container for scrolled state */}
          {isScrolled && (
            // Place controls on the far right when scrolled
            <div className="flex-shrink-0 flex items-center pr-6">
              {" "}
              {/* Add pr-6 */}
              {/* Use children directly */}
              {children}
            </div>
          )}
        </div>
      </div>

      {/* Active filters summary - only visible when not scrolled */}
      {/* Add px-6 to match parent padding */}
      {!isScrolled && <div className="px-6">{activeFiltersSection}</div>}
    </>
  );
}

// Memoize the component export
export const MemoizedFilterHeader = memo(FilterHeader);
MemoizedFilterHeader.displayName = "FilterHeader";
// If FilterHeader was the default export, update the parent component (ComponentBrowserHeader)
// to import MemoizedFilterHeader instead.
